import React, { useEffect, useRef, useState } from 'react';
import './Reveal.css';

/**
 * Reveal Component: Animates children when they come into view
 * @param {string} animation - Animation type: 'fade', 'slideUp', 'slideDown', 'slideLeft', 'slideRight', 'zoomIn', 'rotate'
 * @param {number} duration - Animation duration in milliseconds (default: 1000)
 * @param {number} delay - Animation delay in milliseconds (default: 0)
 * @param {boolean} cascade - For multiple children, stagger animations (default: false)
 */
export default function Reveal({
  children,
  animation = 'fade',
  duration = 1000,
  delay = 0,
  cascade = false,
  ...props
}) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  const animationClass = `reveal-${animation}`;
  const style = {
    '--reveal-duration': `${duration}ms`,
    '--reveal-delay': `${delay}ms`,
  };

  return (
    <div
      ref={ref}
      className={`reveal ${isVisible ? animationClass : 'reveal-hidden'}`}
      style={style}
      {...props}
    >
      {cascade && Array.isArray(children)
        ? children.map((child, index) => (
            <div
              key={index}
              className="reveal-item"
              style={{ '--item-delay': `${index * 100}ms` }}
            >
              {child}
            </div>
          ))
        : children}
    </div>
  );
}
